import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    IconButton,
    Menu,
    MenuItem,
    Stack,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Download as DownloadIcon } from '@mui/icons-material';
import { getGiftCardTransactions, downloadGiftCardTransactions } from '../services/reportService';
import { format } from 'date-fns';

const GiftCardTransactionReport = () => {
    // State for date range
    const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());

    // State for data and sorting
    const [transactions, setTransactions] = useState([]);
    const [sortBy, setSortBy] = useState('TransactionDate');
    const [sortOrder, setSortOrder] = useState('DESC');
    
    // Pagination state
    const [page, setPage] = useState(0); // MUI uses 0-based pages
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // State for export menu
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Load data
    const loadTransactions = async () => {
        try {
            // Set start date to beginning of day to include all transactions on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all transactions on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            const response = await getGiftCardTransactions(
                startOfDay, 
                endOfDay, 
                sortBy, 
                sortOrder,
                page + 1, // Convert to 1-based for API
                pageSize
            );
            setTransactions(response.transactions);
            setTotalCount(response.pagination.totalCount);
        } catch (error) {
            console.error('Failed to load transactions:', error);
            // TODO: Add error notification
        }
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Effect to load data when dependencies change
    useEffect(() => {
        loadTransactions();
    }, [startDate, endDate, sortBy, sortOrder, page, pageSize]);

    // Handle sort change
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'ASC';
        setSortOrder(isAsc ? 'DESC' : 'ASC');
        setSortBy(column);
    };

    // Handle export menu
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        try {
            // Set start date to beginning of day to include all transactions on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all transactions on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            await downloadGiftCardTransactions(startOfDay, endOfDay, format);
        } catch (error) {
            console.error(`Failed to export as ${format}:`, error);
            // TODO: Add error notification
        }
        handleExportClose();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Gift Card Transaction Report
            </Typography>

            {/* Date Range and Export Controls */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <DesktopDatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={setStartDate}
                        slotProps={{ textField: { variant: 'outlined' } }}
                    />
                    <DesktopDatePicker
                        label="End Date"
                        value={endDate}
                        onChange={setEndDate}
                        slotProps={{ textField: { variant: 'outlined' } }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportClick}
                    >
                        Export
                    </Button>
                </Stack>
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
            >
                <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
                <MenuItem onClick={() => handleExport('excel')}>Excel</MenuItem>
                <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
            </Menu>

            {/* Transactions Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'GiftcardNo'}
                                    direction={sortBy === 'GiftcardNo' ? sortOrder.toLowerCase() : 'asc'}
                                    onClick={() => handleSort('GiftcardNo')}
                                >
                                    Gift Card No
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'TransactionDate'}
                                    direction={sortBy === 'TransactionDate' ? sortOrder.toLowerCase() : 'asc'}
                                    onClick={() => handleSort('TransactionDate')}
                                >
                                    Transaction Time
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'POSTransactionNo'}
                                    direction={sortBy === 'POSTransactionNo' ? sortOrder.toLowerCase() : 'asc'}
                                    onClick={() => handleSort('POSTransactionNo')}
                                >
                                    POS Transaction ID
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'StoreName'}
                                    direction={sortBy === 'StoreName' ? sortOrder.toLowerCase() : 'asc'}
                                    onClick={() => handleSort('StoreName')}
                                >
                                    Store
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'Amount'}
                                    direction={sortBy === 'Amount' ? sortOrder.toLowerCase() : 'asc'}
                                    onClick={() => handleSort('Amount')}
                                >
                                    Amount
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.map((transaction) => (
                            <TableRow key={`${transaction.GiftcardNo}-${transaction.TransactionDate}`}>
                                <TableCell>{transaction.GiftcardNo}</TableCell>
                                <TableCell>{transaction.TransactionDate ? transaction.TransactionDate.replace('T', ' ').replace(/\.\d{3}Z?$/, '') : '-'}</TableCell>
                                <TableCell>{transaction.POSTransactionNo}</TableCell>
                                <TableCell>{transaction.StoreName}</TableCell>
                                <TableCell>${transaction.Amount.toFixed(2)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`}
                showFirstButton
                showLastButton
                disabled={totalCount === 0}
            />
        </Box>
    );
};

export default GiftCardTransactionReport;
